<template>
  <div class="mall-history">
    <div class="page">
      <MallHeader :title="'이용내역'" @back-click="goBack">
        <MallIconButton slot="right" :icon="shoppingIcon" :alt="'장바구니'" @click="goCart" />
      </MallHeader>

      <div v-if="histories.length > 0" class="mall-history__list">
        <MallHistoryItem
          v-for="history in histories"
          :key="history.id"
          :history="history"
          @detail-click="goOrderDetail"
          @add-same-click="addSameItems"
          @document-click="onDocumentClick"
        />
      </div>

      <!-- 이용내역이 없거나 불러오지 못했을 때 -->
      <div v-else-if="isLoaded" class="mall-history__empty">
        <span class="mall-history__empty-title">{{ isLoadFailed ? '이용내역을 불러오지 못했어요' : '아직 이용내역이 없어요' }}</span>
        <span class="mall-history__empty-message">{{ isLoadFailed ? '잠시 후 다시 시도해 주세요.' : '컨시어지몰에서 구매한 내역이 여기에 보여요.' }}</span>
      </div>

      <!-- 같은 상품 담기에서 일부 상품을 담지 못했을 때 -->
      <MallConfirmPopup
        v-if="copyResult"
        :icon="warningIcon"
        :title="copyResult.title"
        :messageLines="copyResult.messageLines"
        :cancelLabel="'닫기'"
        :confirmLabel="'장바구니 확인'"
        @close="copyResult = null"
        @cancel="copyResult = null"
        @confirm="goCart"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import mall from '@/utilities/mall.js'
import MallIconButton from '@/components/new/atoms/MallIconButton.vue'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallConfirmPopup from '@/components/new/molecules/MallConfirmPopup.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallHistoryItem from '@/components/new/organisms/MallHistoryItem.vue'

// TODO: 페이지당 최대 100건 - 더 많으면 다음 페이지 조회 필요
var ORDER_LIMIT = 100

export default {
  name: 'MallHistory',
  components: {
    MallIconButton,
    MallHeader,
    MallConfirmPopup,
    MallToast,
    MallHistoryItem,
  },
  data() {
    return {
      profileId: Number(this.$route.query.profileId) || null,
      shoppingIcon: require('@/assets/mall/icn_shopping_line.svg'),
      warningIcon: require('@/assets/mall/img_warning_triangle_3d.png'),

      // 주문 { id, type: 'order', receptionNumber, date, title, canceled(환불), amount, items: [{ name, quantity }] }
      // TODO: 진료 내역(이용내역 API)은 구매 진료도 함께 내려와 주문과 겹쳐서 아직 넣지 않음
      histories: [],
      isLoaded: false,
      isLoadFailed: false,
      isRequesting: false,
      // 같은 상품 담기 결과 안내 { title, messageLines }
      copyResult: null,
      toast: null,
    }
  },
  mounted() {
    ChannelIO('shutdown')

    var token = mall.getAccessToken()
    if (!token) {
      mall.goLogin(this.$router)
      return
    }
    this.fetchHistories(token)
  },
  methods: {
    fetchHistories(token) {
      // 주문 제목(카테고리명)을 찾는 데만 쓰므로 상품 목록 조회가 실패해도 이용내역은 보여줌
      var catalogRequest = mall.fetchCatalog(this.$store, this.profileId, token).catch(() => null)
      var ordersRequest = this.$store.dispatch(Constant.FETCH_STORE_ORDERS, {
        profile_id: this.profileId,
        page: 1,
        limit: ORDER_LIMIT,
        token: token,
      })

      Promise.all([ordersRequest, catalogRequest])
        .then(([data, catalog]) => {
          this.histories = ((data && data.orders) || []).map(order => this.toHistory(order, catalog))
          this.isLoaded = true
        })
        .catch((err) => {
          if (mall.isAuthError(err)) {
            mall.goLogin(this.$router)
            return
          }
          this.isLoadFailed = true
          this.isLoaded = true
        })
    },
    toHistory(order, catalog) {
      var payments = order.payments || []
      return {
        id: order.reception_number,
        type: 'order',
        receptionNumber: order.reception_number,
        date: mall.formatDate(order.purchased_at),
        title: this.findCategoryName(order.products, catalog) || '컨시어지몰 주문',
        canceled: order.is_refund,
        // 결제별 실청구액은 payments[].amount (최신 먼저) - 원장 금액(patient_cost)은 현재 구매 금액이라 과거 결제와 다를 수 있음
        amount: payments.length > 0 ? payments[0].amount : order.patient_cost,
        items: order.products || [],
      }
    },
    // 첫 번째 주문상품의 카테고리명 (주문 상품에는 이름만 있어 상품명으로 찾음)
    // TODO: 주문 상품에 product_id / category_id가 내려오면 이름 대신 id로 찾기
    findCategoryName(products, catalog) {
      if (!catalog || !products || products.length === 0) return ''
      var product = catalog.products.find(item => item.name === products[0].name)
      if (!product) return ''
      var category = catalog.categories.find(item => item.id === product.category_id)
      return category ? category.name : ''
    },
    goBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/shop', query: { profileId: this.profileId } })
    },
    goOrderDetail(history) {
      this.$router.push({ path: '/mall/history/order', query: { profileId: this.profileId, receptionNumber: history.receptionNumber } })
    },
    goCart() {
      this.$router.push({ path: '/mall/cart', query: { profileId: this.profileId } })
    },
    // 주문한 상품을 새 장바구니로 복사 (기존 장바구니는 비워지고 교체됨)
    addSameItems(history) {
      var token = mall.getAccessToken()
      if (!token) {
        mall.goLogin(this.$router)
        return
      }
      if (this.isRequesting) return
      this.isRequesting = true
      this.$store.dispatch(Constant.COPY_CART_FROM_ORDER, {
        reception_number: history.receptionNumber,
        profile_id: this.profileId,
        token: token,
      })
        .then((result) => {
          this.isRequesting = false
          var copyResult = this.toCopyResult(result)
          if (copyResult) {
            this.copyResult = copyResult
          } else {
            this.goCart()
          }
        })
        .catch((err) => {
          this.isRequesting = false
          this.handleError(err, '장바구니에 담지 못했어요.')
        })
    },
    // 담지 못했거나 수량을 줄인 상품이 있으면 안내 (모두 담겼으면 null)
    toCopyResult(result) {
      var excluded = (result && result.excluded) || []
      var adjusted = (result && result.adjusted) || []
      var copied = (result && result.copied) || []
      // 담긴 상품이 없으면 제외 사유가 없어도 안내 (빈 장바구니로 이동하지 않도록)
      if (copied.length === 0) {
        return {
          title: '장바구니에 담을 수 있는 상품이 없어요',
          messageLines: ['주문한 상품이 판매가 끝났거나', '지금은 담을 수 없는 상품이에요.'],
        }
      }
      if (excluded.length === 0 && adjusted.length === 0) return null

      var messageLines = []
      if (excluded.length > 0) {
        messageLines.push('판매가 끝났거나 담을 수 없는 상품 ' + excluded.length + '개는', '빼고 담았어요.')
      }
      if (adjusted.length > 0) {
        messageLines.push('최대 구매 수량을 넘은 상품 ' + adjusted.length + '개는', '수량을 줄여서 담았어요.')
      }
      return {
        title: '일부 상품만 장바구니에 담았어요',
        messageLines: messageLines,
      }
    },
    // 구매 서류 = 계산서(거래명세서), 발행 완료(ISSUED)일 때만 문서가 있음
    onDocumentClick(history) {
      var token = mall.getAccessToken()
      if (!token) {
        mall.goLogin(this.$router)
        return
      }
      if (this.isRequesting) return
      this.isRequesting = true
      this.$store.dispatch(Constant.FETCH_ORDER_STATEMENT, {
        reception_number: history.receptionNumber,
        profile_id: this.profileId,
        token: token,
      })
        .then((statement) => {
          this.isRequesting = false
          var documents = (statement && statement.documents) || []
          if (documents.length > 0) {
            // TODO: 기기 종류별로 여러 건이면 선택해서 열 수 있도록 (지금은 첫 번째 문서)
            this.openDocument(documents[0].url)
            return
          }
          var title = statement && statement.status === 'PENDING' ? '계산서를 발행하고 있어요' : '계산서가 아직 없어요'
          this.showToast(title, '')
        })
        .catch((err) => {
          this.isRequesting = false
          this.handleError(err, '서류를 불러오지 못했어요.')
        })
    },
    openDocument(url) {
      // 팝업이 막히면 현재 창에서 열기
      var opened = window.open(url, '_blank')
      if (!opened) {
        window.location.href = url
      }
    },
    handleError(err, title) {
      if (mall.isAuthError(err)) {
        mall.goLogin(this.$router)
        return
      }
      this.showToast(title, mall.errorMessage(err))
    },
    showToast(title, message) {
      this.toast = { key: Date.now(), title: title, message: message }
    },
  },
}
</script>

<style scoped>
  .mall-history {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
    background: #F2F4F6;
  }
  .page {
    width: 100%;
    min-height: 100vh;
    background: #F2F4F6;
  }
  .mall-history__list {
    display: flex;
    padding: 4px 16px 32px 16px;
    flex-direction: column;
    gap: 4px;
  }
  /* 헤더(64px) 아래 남은 영역의 가운데쯤 */
  .mall-history__empty {
    display: flex;
    padding: 160px 16px 0 16px;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    font-family: Pretendard;
    font-weight: var(--font-weight-medium);
    text-align: center;
  }
  .mall-history__empty-title {
    color: var(--color-text-main);
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-normal);
  }
  .mall-history__empty-message {
    color: #6B7684;
    font-size: var(--font-size-sm);
    line-height: var(--line-height-normal);
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallHistory.vue